import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AlarmLevel, AlarmLevelKey } from 'ops/common/models/alarm-level';
import { AlarmSetting, AlarmSettingFns } from 'ops/common/models/alarm-setting';
import { Channel, ChannelFns } from 'ops/common/models/channel';
import { ObjectSchema, ValidationError, scaleErrorValues } from 'ops/common/validators';

@Component({
    selector: 'app-alarm-setting-form',
    templateUrl: './alarm-setting-form.component.html',
    styleUrls: ['./alarm-setting-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AlarmSettingFormComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class AlarmSettingFormComponent implements OnChanges, OnInit {
    readonly ALARM_ALARM = AlarmLevelKey[AlarmLevel.Alarm];
    readonly ALARM_WARNING = AlarmLevelKey[AlarmLevel.Warning];
    readonly ALARM_CAUTION = AlarmLevelKey[AlarmLevel.Caution];
    AlarmLevels = AlarmLevel;

    @Output() onSubmit = new EventEmitter<AlarmSetting>();
    @Output() onDelete = new EventEmitter<AlarmSetting>();

    @Input() userPermission: boolean = false;
    @Input() alarm: AlarmSetting;
    @Input() channel: Channel;
    @Input() validator: ObjectSchema<AlarmSetting>;
    @Input() measurementUnits = '';
    @Input() isFeedbackAlarm: boolean = false;
    @Input() disableFields: string[] = [];
    @Input() title = '';

    initAlarm: AlarmSetting;

    alarmInput: AlarmSetting;
    alarmLevelText = AlarmLevelKey[AlarmLevel.None];

    validationErrors: { [propertyName: string]: ValidationError } = {};
    errorCount: number = 0;
    disabledFieldSet = new Set<string>();
    enableEdit: boolean = false;

    checkSetPoint: boolean = false;
    checkDelay: boolean = false;
    checkAlarmServerity: boolean = false
    checkTimer: boolean = false;

    constructor(private translationService: TranslateService) {}

    ngOnInit(): void {
        this.initAlarm = this.cloneAlarm(this.alarm);
        this.alarmLevelText = AlarmLevelKey[this.alarm.alarmLevel];
        this.validate();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(changes.alarm) {
            this.initAlarm = this.cloneAlarm(this.alarm);
        }

        if (changes.disableFields) {
            this.disabledFieldSet = new Set(this.disableFields);
        }

        if (changes.userPermission && !this.userPermission && this.enableEdit) {
            this.alarmCancelButtonClick();
        }

        if(changes.channel) {
            this.enableEdit = false;
            const previewContainerList = document.querySelectorAll('.preview-container');
            previewContainerList.forEach(element => {
                (element as HTMLElement).style.opacity = '1';
                (element as HTMLElement).style.pointerEvents = 'auto';
            });
        }
    }

    editCheckExistDelayButtonDisabled() {
        let isDisabled = false;
        
        if(this.isEnabled('setPoint')) {
            isDisabled = !this.checkSetPoint;
        }
        if(this.isEnabled('timer')) {
            isDisabled = !this.checkTimer;
        }

        return isDisabled || !this.checkDelay || !this.checkAlarmServerity;
    }

    editTextClick() {
        const previewContainerList = document.querySelectorAll('.preview-container');
        previewContainerList.forEach(element => {
            (element as HTMLElement).style.opacity = '0.3';
            (element as HTMLElement).style.pointerEvents = 'none';
        });
        this.enableEdit = true;
    }

    alarmCancelButtonClick() {
        this.enableEdit = false;
        this.alarm = this.cloneAlarm(this.initAlarm);
        const previewContainerList = document.querySelectorAll('.preview-container');
        previewContainerList.forEach(element => {
            (element as HTMLElement).style.opacity = '1';
            (element as HTMLElement).style.pointerEvents = 'auto';
        });
    }

    validate() {
        const errors = this.getErrors(this.alarm);
        this.setErrors(errors);

        this.checkSetPoint = (Boolean(this.alarm.setPoint) || this.alarm.setPoint === 0) && !this.validationErrors?.setPoint?.message;
        this.checkTimer = (Boolean(this.alarm.setPoint) || this.alarm.setPoint === 0) && !this.validationErrors?.setPoint?.message;
        this.checkDelay = (Boolean(this.alarm.delayTimer) || this.alarm.delayTimer === 0) && !this.validationErrors?.delayTimer?.message;
        this.checkAlarmServerity = this.alarm.alarmLevel !== AlarmLevel.None;
    }

    private getErrors(alarm: AlarmSetting): ValidationError[] {
        try {
            this.validator.validateSync(this.alarm, { abortEarly: false });
            return [];
        } catch (e) {
            if (e.name !== 'ValidationError') {
                console.error(e);
                return [];
            }
            
            scaleErrorValues(this.channel, e);

            return (e as ValidationError).inner;
        }
    }

    private setErrors(errors: ValidationError[]) {
        const newErrors = {};

        for (let i = 0; i < errors.length; i++) {
            newErrors[errors[i].path] = errors[i];
        }

        this.errorCount = errors.length;
        this.validationErrors = newErrors;
    }

    isDigital() { return ChannelFns.isDigital(this.channel); }

    isFormDataValid(): boolean {
        return this.errorCount == 0;
    }

    saveButtonClick(enable: boolean) {
        this.alarm.enable = enable;
        this.submit();
    }

    submit(): void {
        const alarmValues = this.cloneAlarm(this.alarm);
        this.onSubmit.emit(alarmValues);
    }

    closeComponent() {
        this.enableEdit = false;
        const previewContainerList = document.querySelectorAll('.preview-container');
        previewContainerList.forEach(element => {
            (element as HTMLElement).style.opacity = '1';
            (element as HTMLElement).style.pointerEvents = 'auto';
        });
    }

    submitDelete() {
        const alarmValues = this.cloneAlarm(this.alarmInput);
        alarmValues.enable = false;

        const errors = this.getErrors(alarmValues);

        if (errors.length == 0) {
            this.onDelete.emit(alarmValues);
        }
    }

    public cloneAlarm(alarm: AlarmSetting): AlarmSetting {
        return AlarmSettingFns.clone(alarm);
    }

    hasErrorString = (propName: string) => propName in this.validationErrors;
    getErrorString = (propName: string) => {
        if (!this.hasErrorString(propName)) return '';

        const err = this.validationErrors[propName];
        return this.translationService.instant(err.errors[0], err.params);
    };

    isEnabled(fieldName) {
        return !this.disabledFieldSet.has(fieldName);
    }

    isExistingAlarm() {
        return this.alarm.enable;
    }

    onInputChange(event) {
        if(event.name === 'setPoint') {
            this.alarm.setPoint = event.value;
        }else if(event.name === 'timer') {
            this.alarm.setPoint = event.value;
        }else if(event.name === 'delay') {
            this.alarm.delayTimer = event.value;
        }else if(event.name === 'extG') {
            this.alarm.alarmGroupNo = event.value;
        }else if(event.name === 'gRep1') {
            this.alarm.reposeGroup1No = event.value;
        }else if(event.name === 'gRep2') {
            this.alarm.reposeGroup2No = event.value;
        }

        this.validate();
    }

    changeAlarmLevel() {
        this.alarmLevelText = AlarmLevelKey[this.alarm.alarmLevel];
    }

    getScale() { return this.channel.fcuData.scale; }
    getMeasurementUnits() { return this.channel.opsData.measurementUnit; }
}
